import { unwrap } from 'solid-js/store';
import type { Setter } from 'solid-js';
import { type Mode } from './PosterMode';
import { handleRender } from './renderPoster';
import { createUploadState } from '../canvas/images/upload';
import { assignIds, migrateGeometry } from '../canvas/utils';
import { type IPosterBuilder } from './PosterBuilder';
import type { RuntimeObject, DataRecord } from '../types';
import type { Poster } from './posterSchema';
import { themeVariablesToRecord, type ThemeVariable } from './themeVariables';
import { batch } from 'solid-js';
import { css } from '@emotion/css';
import { Switch, Match, Show, createSignal, onMount } from 'solid-js';
import { FontLoader } from '../fonts/FontLoader';
import { CanvasWrap } from '../canvas/CanvasWrap';
import { EditablePosterSvg } from 'Shared/posters/project/EditablePosterSvg';
import { ReadOnlyPosterSvg } from 'Shared/posters/project/ReadOnlyPosterSvg';

//XXX setDesign is basically a setStore call
/* The three writes replace the design's slices with fresh objects, so every <For> row is keyed on a
   new value and remounts itself — no load counter needed to force it. */
export function resetPoster(design,setDesign)
{
	const objs = (design?.objects ?? []) as RuntimeObject[];
	migrateGeometry(objs);
	assignIds(objs);
	batch(() => {
		setDesign('objects', objs);
		setDesign('canvas', design?.canvas ?? { width: 400, height: 600, background: '#000' }); //FIXME pass in as parameters?
	});
}

/* The nested design being switched away from is held as the user preview's *shadow*. It serves both
   roles at once: it restores design mode on the way back, and while the preview is flat (the store is
   hoisted on entering user mode — see UserViewFlattener) it is the layout authority those flat leaves
   reflow against. That is what makes the preview a real test of the layout rather than a frozen
   snapshot of it — the same arrangement the event page runs with its template. */
export function switchModes(mode,setMode,design,setDesign,shadow,setShadow)
{
	switch(mode) {
		case 'design':
			setShadow(structuredClone(unwrap(design)) as Poster);
			setMode('user');
			return;

		case 'user':
			const snap = shadow();
			setMode('design');
			batch(() => {
				setDesign('objects', (snap?.objects ?? []) as RuntimeObject[]);
				setDesign('canvas', snap?.canvas ?? { width: 400, height: 600, background: '#000' }); //FIXME pass in as parameters?
			});
			setShadow(undefined);
	}
}


interface IReadOnlyPoster {
	builder: IPosterBuilder;
	record:                 unknown;
	/** Theme variables, merged into the record under a `theme` key for field lookups. */
	theme?:                 ThemeVariable[];
}

export function ReadOnlyPoster(P: IReadOnlyPoster)
{
//TODO poster ==> posterDesignName

	const textRefs: Record<string, SVGTextElement> = {};

	const theme = () => P.theme?.length ? themeVariablesToRecord(P.theme) : undefined;

	/* A poster wraps and fits its text against canvas metrics and getBBox, so it can only be drawn
	   in a browser — the gate is here rather than in the pages that show one, so no caller can put a
	   poster into the SSR pass by forgetting it. See PosterCanvas for why it is a signal. */
	const [mounted,setMounted] = createSignal(false);
	onMount(() => setMounted(true));

	/* Loading the fonts belongs to the poster, not to the pages that show one: without this the
	   text falls back to the system font. */
	return (<Show when={mounted()}>
		<FontLoader objects={P.builder.objects} fonts={P.builder.fonts} theme={theme()} />

		<ReadOnlyPosterSvg {...P.builder} textRefs={textRefs} record={P.record as DataRecord} theme={theme()} mode='read' />
	</Show>);
}


// ── PosterRenderer ────────────────────────────────────────────────────────────

interface IPosterRenderer {
	builder: IPosterBuilder;
	record:                unknown;
	theme?:                ThemeVariable[];
	posterId?:             string;
	/** 'event' uses the Event-image folder; 'special' uses Special-image. */
	mode:              Mode;
	setMode:           Setter<Mode>;
	registerRender?:       (render: () => void) => void;
	markImagesShared:     boolean;
	savePage?:             string;
	savePermission?:       string;
	saveLocation?:         string[];
	saveOnlyInDesignMode?: boolean;
	imageFolder: string;
}

export interface ISave {
	page?:             string;
	permission?:       string;
	location?:         string[];
	onlyInDesignMode?: boolean;
}

export function PosterCanvas(P: IPosterRenderer)
{
	const textRefs: Record<string, SVGTextElement> = {};

	// The theme variables resolved to a name→value lookup, threaded alongside the record
	// so bound properties/fields resolve against it without polluting the record itself.
	// Undefined when there are no variables, so the resolvers take their no-theme fast path.
	const theme = () => P.theme?.length ? themeVariablesToRecord(P.theme) : undefined;

	const upload = createUploadState({page:P.savePage, folder:P.imageFolder, shared: P.markImagesShared });

	const save = () => ({
		page: P.savePage,
		permission: P.savePermission,
		location: P.saveLocation,
		onlyInDesignMode: P.saveOnlyInDesignMode
	} as ISave);

	if (P.registerRender)
		P.registerRender(() => handleRender(P.builder.drag));

	/* The builder is a DOM editor: it measures text with getBBox and a canvas context, binds document
	   listeners, hit-tests with elementsFromPoint and portals its fonts into <head>. None of that
	   exists on the server, so the canvas is kept out of the SSR pass entirely and mounts on the
	   client instead. A signal rather than isServer: the server must emit the same empty branch the
	   client hydrates against, and only then can the canvas appear. Public pages show a poster
	   through ReadOnlyPoster, which stays server-rendered. */
	const [mounted,setMounted] = createSignal(false);
	onMount(() => setMounted(true));

	return (<Show when={mounted()}>
		<FontLoader objects={P.builder.objects} fonts={P.builder.fonts} theme={theme()} />

		<Switch>
			<Match when={P.mode=='read'}>
				<ReadOnlyPosterSvg {...P.builder}
					textRefs={textRefs} record={P.record as DataRecord} theme={theme()}
					mode={P.mode} posterId={P.posterId!} save={save()}
				/>
			</Match>
			<Match when={P.mode=='design' || P.mode=='user'}>
				<CanvasWrap objects={P.builder.objects}
					record={P.record as DataRecord} theme={theme()!} mode={P.mode}
				>
					<EditablePosterSvg {...P.builder}
						upload={upload} textRefs={textRefs} record={P.record as DataRecord} theme={theme()}
						mode={P.mode} posterId={P.posterId!} save={save()}
					/>
				</CanvasWrap>
			</Match>
		</Switch>
	</Show>);
}

export function PosterHints()
{
	const hintStyle = css({
		marginTop: 16,
		fontSize: 11,
		color: '#555',
		textAlign: 'center',
		letterSpacing: '.5px',
	});

	// Lazy so it's only evaluated client-side — `navigator` is undefined during SSR.
	const isMac = () => navigator.platform.startsWith('Mac') || /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)

	return (
		<p class={hintStyle}>Double-click to edit content · {isMac() ? '⌘' : 'Ctrl'}-click to select multiple · Shift-click to drag image within viewport · {isMac() ? '⌥' : 'Alt'}-click to cycle selection · Esc to deselect</p>
	);
}

